// One React Query key per object's permissions, whatever form its id arrives
// in: Logistics rows carry string ids ("64"), other pages numbers (64). The
// single and batch permission hooks both read and write through this, so a
// batch result is found by every card instead of being fetched again.

/**
 * @param {string} contentType - e.g. "equipment", "tier", "panelcomponent"
 * @param {string | number} objectId
 * @returns {[string, string, number]}
 */
export const permissionsKey = (contentType, objectId) => [
  "permissions",
  contentType,
  Number(objectId),
];

/**
 * Retry only failures that may pass next time (network errors, no response);
 * a 4xx/5xx answer won't change on retry and each retry delayed the page.
 *
 * @param {number} failureCount
 * @param {{ response?: { status?: number } }} error
 * @returns {boolean}
 */
export const retryPermissionsRequest = (failureCount, error) =>
  !error?.response && failureCount < 2;

/**
 * Writes each row's `permissions` (computed by the server for list pages)
 * into the permissions cache, so cards and pages find them without the bulk
 * permission request. Rows without permissions are left alone.
 *
 * @param {import("@tanstack/react-query").QueryClient} queryClient
 * @param {string} contentType - e.g. "equipment", "tier", "instrument"
 * @param {{ id: string | number, permissions?: object | null }[]} rows
 */
export const seedPermissions = (queryClient, contentType, rows = []) => {
  rows.forEach((row) => {
    const { permissions } = row ?? {};
    if (!permissions) return;
    const accessLevel = permissions.can_delete
      ? "Owner"
      : permissions.can_edit
        ? "Editor"
        : permissions.can_view
          ? "Viewer"
          : null;
    queryClient.setQueryData(permissionsKey(contentType, row.id), {
      permissions,
      access_level: accessLevel,
    });
  });
};
